import { CONNECTION_QUALITY, CONNECTION_STATUS, TIMING } from '../constants';
import { connectionQualityChanged, connectionStatusChanged } from '../state/slice';
import { probe } from './probe';

/**
 * The connection banner's rules. This is the whole feature:
 *
 * 1. The browser says offline, or a real request fails with no response → check /health.
 *    Two failed checks, 1 s apart → Offline, unless they ran out of time and a real request got an
 *    answer meanwhile.
 * 2. While Offline → check /health every 5 s until it answers.
 * 3. The browser says online again → Reconnecting, and check now. A failed check → back to Offline.
 * 4. A check answers while Offline or Reconnecting → Back online, hidden after 3 s. So does a real
 *    answer, when the last check ran out of time.
 * 5. A real request still waiting after 8 s → one timed check. Over 2 s → Slow.
 * 6. Slow hides once no request is waiting.
 *
 * While online and idle, nothing is called.
 *
 * A check that gets no answer within 5 s counts as failed, but on a very slow link real requests
 * still get answers. Any answer, an error status included, proves the connection works, so it
 * outranks a check that ran out of time. Never one that failed outright: that is conclusive, and the
 * answer may be the late reply to a request sent just before the connection dropped.
 *
 * For the HTTP layer: `waitForOnline` holds a GET while a check is running or the user is offline,
 * and `getOfflineMs` tells whether the user went offline while a request was out.
 */

const { ONLINE, OFFLINE, RECONNECTING, BACK_ONLINE } = CONNECTION_STATUS;

let dispatch = null;
let status = ONLINE;
let slow = false;
let confirming = false;
let inFlightCheck = null;
let offlineRetryTimer = null;
let backOnlineTimer = null;
let offlineSince = null;
let offlineTotalMs = 0;
let lastAnswerAt = 0;
let lastCheckTimedOut = false;
const waitingRequests = new Map();
const onlineWaiters = new Set();

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const canSend = () => status === ONLINE || status === BACK_ONLINE;

// A GET waits out a running check too: most checks clear, and then it goes straight out.
const isReady = () => canSend() && !confirming;

const releaseWaiters = () => {
    if (!isReady()) return;
    onlineWaiters.forEach((resolve) => resolve());
    onlineWaiters.clear();
};

// Two callers asking at the same moment share one request.
const check = () => {
    if (!inFlightCheck) {
        inFlightCheck = probe()
            .then((result) => {
                lastCheckTimedOut = result.timedOut;
                return result;
            })
            .finally(() => {
                inFlightCheck = null;
            });
    }
    return inFlightCheck;
};

const setStatus = (next) => {
    if (next === status) return;
    const couldSend = canSend();
    status = next;
    dispatch?.(connectionStatusChanged(next));

    if (couldSend && !canSend()) offlineSince = Date.now();
    if (!couldSend && canSend()) {
        offlineTotalMs += Date.now() - offlineSince;
        offlineSince = null;
    }
    releaseWaiters();
};

const setSlow = (next) => {
    if (next === slow) return;
    slow = next;
    dispatch?.(connectionQualityChanged(next ? CONNECTION_QUALITY.SLOW : CONNECTION_QUALITY.GOOD));
};

const isDown = () => status === OFFLINE || status === RECONNECTING;

// Rule 4
const showBackOnline = () => {
    clearTimeout(offlineRetryTimer);
    setStatus(BACK_ONLINE);
    clearTimeout(backOnlineTimer);
    backOnlineTimer = setTimeout(() => {
        if (status === BACK_ONLINE) setStatus(ONLINE);
    }, TIMING.BACK_ONLINE_DISPLAY_MS);
};

// Rules 2 and 3: one check while offline; success ends it, failure schedules the next.
const checkWhileOffline = async () => {
    clearTimeout(offlineRetryTimer);
    const { reachable } = await check();
    if (!isDown()) return;

    if (reachable) {
        showBackOnline();
        return;
    }

    setStatus(OFFLINE);
    offlineRetryTimer = setTimeout(checkWhileOffline, TIMING.OFFLINE_RETRY_MS);
};

/**
 * Rule 1.
 * @param {number} since  when the first failed check went out; a real answer after it cancels Offline
 */
const confirmOffline = async (since = Date.now()) => {
    const answeredSince = () => lastCheckTimedOut && lastAnswerAt >= since;
    if (!dispatch || confirming || !canSend() || answeredSince()) return;
    confirming = true;

    let result = await check();
    if (!result.reachable && !answeredSince()) {
        await wait(TIMING.CONFIRM_RETRY_MS);
        result = await check();
    }

    confirming = false;
    if (result.reachable || answeredSince() || !canSend()) {
        releaseWaiters();
        return;
    }

    setSlow(false);
    setStatus(OFFLINE);
    offlineRetryTimer = setTimeout(checkWhileOffline, TIMING.OFFLINE_RETRY_MS);
};

// Rule 5. The request's own wait includes the server's work, so the timed check decides.
const checkSlowRequest = async () => {
    if (!canSend()) return;

    const since = Date.now();
    const { reachable, ms } = await check();
    if (!reachable) {
        void confirmOffline(since);
        return;
    }
    if (ms != null && ms > TIMING.SLOW_CHECK_MS && waitingRequests.size > 0) setSlow(true);
};

const handleOffline = () => confirmOffline();

const handleOnline = () => {
    if (status !== OFFLINE) return;
    setStatus(RECONNECTING);
    void checkWhileOffline();
};

/** Starts listening. Called once from the store; safe to call again. */
export const startConnectivityMonitor = (storeDispatch) => {
    if (dispatch) return;
    dispatch = storeDispatch;

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    if (navigator.onLine === false) void confirmOffline();
};

export const stopConnectivityMonitor = () => {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    clearTimeout(offlineRetryTimer);
    clearTimeout(backOnlineTimer);
    waitingRequests.forEach(clearTimeout);
    waitingRequests.clear();
    onlineWaiters.clear();
    dispatch = null;
    status = ONLINE;
    slow = false;
    confirming = false;
    inFlightCheck = null;
    offlineSince = null;
    offlineTotalMs = 0;
    lastAnswerAt = 0;
    lastCheckTimedOut = false;
};

/** Resolves once requests can go out again; straight away when they already can. */
export const waitForOnline = () =>
    isReady() ? Promise.resolve() : new Promise((resolve) => onlineWaiters.add(resolve));

/** Total time spent offline since the app loaded, the current spell included. */
export const getOfflineMs = () =>
    offlineTotalMs + (offlineSince == null ? 0 : Date.now() - offlineSince);

/** Called by the HTTP layer as a request leaves. */
export const trackRequestStart = (id) => {
    if (!dispatch) return;
    waitingRequests.set(id, setTimeout(checkSlowRequest, TIMING.SLOW_REQUEST_MS));
};

/**
 * Called by the HTTP layer when a request settles.
 * @param {{ answered?: boolean, networkError?: boolean }} outcome  `answered` — the server sent a
 *   response, whatever its status. `networkError` — no response came back at all.
 */
export const trackRequestEnd = (id, { answered = false, networkError = false } = {}) => {
    clearTimeout(waitingRequests.get(id));
    waitingRequests.delete(id);

    // Rule 6
    if (waitingRequests.size === 0) setSlow(false);

    if (answered) {
        lastAnswerAt = Date.now();
        if (dispatch && isDown() && lastCheckTimedOut) showBackOnline();
    }
    if (networkError) confirmOffline();
};
